export type MotivoBloqueioFacial =
  | 'OK'
  | 'VIDEO_INDISPONIVEL'
  | 'SEM_ROSTO'
  | 'APROXIME'
  | 'AFASTE'
  | 'DESCA_ROSTO'
  | 'SUBA_ROSTO'
  | 'CENTRALIZE_HORIZONTAL';

export interface CaixaRosto {
  x: number;
  y: number;
  width: number;
  height: number;
}

export interface DimensoesVideo {
  width: number;
  height: number;
}

export interface ResultadoGateFacial {
  liberado: boolean;
  motivo: MotivoBloqueioFacial;
  desvioHorizontal: number;
  desvioVertical: number;
}

const FRACAO_LARGURA_MINIMA = 1 / 4;
const FRACAO_ALTURA_MINIMA = 1 / 3;
const FRACAO_TOLERANCIA = 1 / 8;
const FRACAO_TOLERANCIA_LEGADA = 1 / 16;

export interface LimitesGateFacial {
  larguraMinima: number;
  alturaMinima: number;
  toleranciaHorizontal: number;
  toleranciaAcima: number;
  toleranciaAbaixo: number;
}

export function calcularLimitesGateFacial(video: DimensoesVideo): LimitesGateFacial {
  const base = Math.min(video.width, video.height);
  const tolerancia = base * FRACAO_TOLERANCIA;
  const toleranciaLegada = video.height * FRACAO_TOLERANCIA_LEGADA;

  return {
    larguraMinima: base * FRACAO_LARGURA_MINIMA,
    alturaMinima: base * FRACAO_ALTURA_MINIMA,
    toleranciaHorizontal: Math.max(tolerancia, toleranciaLegada),
    toleranciaAcima: tolerancia,
    toleranciaAbaixo: Math.max(tolerancia, toleranciaLegada * 3)
  };
}

export function avaliarGateFacial(
  caixa: CaixaRosto | null | undefined,
  video: DimensoesVideo | null | undefined
): ResultadoGateFacial {
  if (!video || video.width <= 0 || video.height <= 0) {
    return { liberado: false, motivo: 'VIDEO_INDISPONIVEL', desvioHorizontal: 0, desvioVertical: 0 };
  }

  if (!caixa) {
    return { liberado: false, motivo: 'SEM_ROSTO', desvioHorizontal: 0, desvioVertical: 0 };
  }

  const limites = calcularLimitesGateFacial(video);

  const desvioHorizontal = caixa.x + caixa.width / 2 - video.width / 2;
  const desvioVertical = caixa.y + caixa.height / 2 - video.height / 2;

  const bloquear = (motivo: MotivoBloqueioFacial): ResultadoGateFacial => ({
    liberado: false,
    motivo,
    desvioHorizontal,
    desvioVertical
  });

  if (caixa.width < limites.larguraMinima || caixa.height < limites.alturaMinima) {
    return bloquear('APROXIME');
  }
  if (desvioVertical < -limites.toleranciaAcima) {
    return bloquear('DESCA_ROSTO');
  }
  if (desvioVertical > limites.toleranciaAbaixo) {
    return bloquear('SUBA_ROSTO');
  }
  if (Math.abs(desvioHorizontal) > limites.toleranciaHorizontal) {
    return bloquear('CENTRALIZE_HORIZONTAL');
  }

  return { liberado: true, motivo: 'OK', desvioHorizontal, desvioVertical };
}

export function extrairCaixaRosto(detection: unknown): CaixaRosto | null {
  const box = (detection as { _box?: Record<string, number>; box?: Record<string, number> })?._box
    ?? (detection as { box?: Record<string, number> })?.box;

  if (!box) {
    return null;
  }

  const x = box._x ?? box.x;
  const y = box._y ?? box.y;
  const width = box._width ?? box.width;
  const height = box._height ?? box.height;

  if (![x, y, width, height].every(valor => typeof valor === 'number' && Number.isFinite(valor))) {
    return null;
  }

  return { x, y, width, height };
}

export interface EstadoHistereseGate {
  liberado: boolean;
  bloqueiosConsecutivos: number;
}

export const TOLERANCIA_BLOQUEIOS_CONSECUTIVOS = 2;

export function aplicarHistereseGate(
  estadoAtual: EstadoHistereseGate,
  resultado: ResultadoGateFacial,
  toleranciaBloqueios: number = TOLERANCIA_BLOQUEIOS_CONSECUTIVOS
): EstadoHistereseGate {
  if (resultado.liberado) {
    return { liberado: true, bloqueiosConsecutivos: 0 };
  }

  const bloqueiosConsecutivos = estadoAtual.bloqueiosConsecutivos + 1;
  const rostoPresente = resultado.motivo !== 'SEM_ROSTO' && resultado.motivo !== 'VIDEO_INDISPONIVEL';
  const dentroDaTolerancia = bloqueiosConsecutivos <= toleranciaBloqueios;

  return {
    liberado: estadoAtual.liberado && rostoPresente && dentroDaTolerancia,
    bloqueiosConsecutivos
  };
}

export function extrairScoreDeteccao(detection: unknown): number | null {
  const bruto = (detection as { _score?: unknown; score?: unknown })?._score
    ?? (detection as { score?: unknown })?.score;

  return typeof bruto === 'number' && Number.isFinite(bruto) ? bruto : null;
}

export function traduzirMotivoBloqueio(
  motivo: MotivoBloqueioFacial,
  desvioHorizontal: number,
  espelhado: boolean
): string {
  switch (motivo) {
    case 'OK':
      return 'FACIAL_ORIENTACAO_PRONTO';
    case 'VIDEO_INDISPONIVEL':
      return 'FACIAL_ORIENTACAO_CARREGANDO';
    case 'SEM_ROSTO':
      return 'FACIAL_ORIENTACAO_SEM_ROSTO';
    case 'APROXIME':
      return 'FACIAL_ORIENTACAO_APROXIME';
    case 'AFASTE':
      return 'FACIAL_ORIENTACAO_AFASTE';
    case 'DESCA_ROSTO':
      return 'FACIAL_ORIENTACAO_DESCA';
    case 'SUBA_ROSTO':
      return 'FACIAL_ORIENTACAO_SUBA';
    case 'CENTRALIZE_HORIZONTAL':
      return resolverDicaHorizontal(desvioHorizontal, espelhado);
    default:
      return 'FACIAL_ORIENTACAO_SEM_ROSTO';
  }
}

export function resolverDicaHorizontal(desvioHorizontal: number, espelhado: boolean): string {
  const precisaIrParaDireita = espelhado ? desvioHorizontal > 0 : desvioHorizontal < 0;
  return precisaIrParaDireita ? 'FACIAL_ORIENTACAO_DIREITA' : 'FACIAL_ORIENTACAO_ESQUERDA';
}

export function descreverOrientacao(video: DimensoesVideo): 'retrato' | 'paisagem' | 'quadrado' {
  if (video.height > video.width) {
    return 'retrato';
  }
  return video.width > video.height ? 'paisagem' : 'quadrado';
}
